import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, XMarkIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  DataTable,
  DataTableColumn,
  DataTableAction,
  FormModal,
  ConfirmationModal,
  Select,
  useToastActions,
} from '@airclou/ui';
import { useTimezone } from '@airclou/shell/timezone';
import { formatDateInTz } from '@airclou/utils';
import { dashboardApiClient, type AccessRequest } from '@/services/api';
import { listWorkspaces, type Workspace } from '@/api/workspaces';
import { RefreshErrorBanner } from '@/components/state/RefreshErrorBanner';

/**
 * People → Requests: people at this organization's company domain asking to
 * join it (docs/plans/one-organization-per-domain-plan.md, Step 3).
 *
 * Approve adds them to a workspace you choose, the way an invitation does.
 * Decline tells them by email. A request nobody answers for 7 days is
 * escalated to Airclou support and shows here as "Waiting on support" — it can
 * still be approved or declined.
 */
export function AccessRequestsTab() {
  const timezone = useTimezone();
  const { t } = useTranslation('dashboard');
  const { success: toastSuccess, error: toastError } = useToastActions();

  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const [toApprove, setToApprove] = useState<AccessRequest | null>(null);
  const [toDecline, setToDecline] = useState<AccessRequest | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [workspacesFailed, setWorkspacesFailed] = useState(false);
  const [workspaceId, setWorkspaceId] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');

  // No dependencies on purpose: a fetch that re-ran whenever a render-time
  // value changed would refetch in a loop, and a later success would quietly
  // replace the failure the admin should be seeing.
  const fetchRequests = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      setRequests(await dashboardApiClient.getAccessRequests());
    } catch (err: any) {
      console.error('Failed to load access requests:', err);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  /** The workspace Approve starts on: the default one, which is where signup put everyone else. */
  const preferredWorkspaceId = useCallback(
    (list: Workspace[]) => (list.find((w) => w.slug === 'default') ?? list[0])?.id ?? '',
    [],
  );

  // Workspaces are only needed once someone opens Approve. A failed load stays
  // failed (and says so in the modal) until the admin retries; it is never
  // shown as an empty list.
  useEffect(() => {
    if (!toApprove || workspaces !== null || workspacesFailed) return;
    listWorkspaces()
      .then((response) => {
        const list = response.workspaces ?? [];
        setWorkspaces(list);
        setWorkspaceId(preferredWorkspaceId(list));
      })
      .catch((err) => {
        console.error('Failed to load workspaces:', err);
        setWorkspacesFailed(true);
      });
  }, [toApprove, workspaces, workspacesFailed, preferredWorkspaceId]);

  /**
   * What to tell the admin about a refusal. The one refusal that keeps the
   * dialog open (the chosen workspace is gone) has its own translated line;
   * anything else gets the generic one, since the server's text is English.
   */
  const serverReason = useCallback(
    (err: any, fallback: string) => (err?.code === 'INVALID_WORKSPACE' ? t('accessRequests.invalidWorkspace') : fallback),
    [t],
  );

  // Another admin answered it first (or it was withdrawn): the row is stale,
  // so close the dialog and reload rather than refuse every retry.
  const alreadyAnswered = useCallback((err: any) => err?.code === 'ACCESS_REQUEST_DECIDED' || err?.status === 404, []);

  const handleApprove = useCallback(async () => {
    if (!toApprove || !workspaceId) return;
    const request = toApprove;
    setActionInFlight(request.id);
    try {
      await dashboardApiClient.approveAccessRequest(request.id, { workspaceId, role });
      setToApprove(null);
      toastSuccess(t('accessRequests.approved', { email: request.email }));
      await fetchRequests();
    } catch (err: any) {
      console.error('Failed to approve access request:', err);
      toastError(serverReason(err, t('accessRequests.failedToApprove')));
      if (alreadyAnswered(err)) {
        setToApprove(null);
        await fetchRequests();
      } else if (err?.code === 'INVALID_WORKSPACE') {
        // The chosen workspace is gone: drop the cached list so the dialog
        // reloads it rather than keep offering the one the server refused.
        setWorkspaces(null);
      }
    } finally {
      setActionInFlight(null);
    }
  }, [toApprove, workspaceId, role, t, toastSuccess, toastError, serverReason, fetchRequests, alreadyAnswered]);

  const handleDecline = useCallback(async () => {
    if (!toDecline) return;
    const request = toDecline;
    setActionInFlight(request.id);
    try {
      await dashboardApiClient.declineAccessRequest(request.id);
      setToDecline(null);
      toastSuccess(t('accessRequests.declined', { email: request.email }));
      await fetchRequests();
    } catch (err: any) {
      console.error('Failed to decline access request:', err);
      toastError(serverReason(err, t('accessRequests.failedToDecline')));
      if (alreadyAnswered(err)) {
        setToDecline(null);
        await fetchRequests();
      }
    } finally {
      setActionInFlight(null);
    }
  }, [toDecline, t, toastSuccess, toastError, serverReason, fetchRequests, alreadyAnswered]);

  const columns: DataTableColumn<AccessRequest>[] = useMemo(
    () => [
      {
        id: 'email',
        header: t('accessRequests.person'),
        accessorKey: 'email',
        enableSorting: false,
        size: 280,
        cell: (request: AccessRequest) => (
          <div>
            <div className="text-sm font-medium text-foreground">{request.email}</div>
            <div className="text-sm text-muted-foreground">
              {t('accessRequests.askedOn', { date: formatDateInTz(request.createdAt, timezone) })}
            </div>
          </div>
        ),
      },
      {
        id: 'status',
        header: t('common.status'),
        accessorKey: 'status',
        enableSorting: false,
        size: 160,
        cell: (request: AccessRequest) =>
          request.status === 'escalated' ? (
            // eslint-disable-next-line airclou/no-hardcoded-theme-colors -- status colour: means "overdue", not brand; both themes stepped
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
              {t('accessRequests.statusEscalated')}
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
              <ClockIcon className="w-3 h-3 mr-1" />
              {t('accessRequests.statusPending')}
            </span>
          ),
      },
    ],
    [t, timezone],
  );

  const actions: DataTableAction<AccessRequest>[] = useMemo(
    () => [
      {
        id: 'approve',
        label: t('accessRequests.approve'),
        icon: CheckIcon,
        onClick: (request: AccessRequest) => {
          // Each approval starts from the defaults, whatever the last one chose.
          setRole('member');
          setWorkspaceId(preferredWorkspaceId(workspaces ?? []));
          setToApprove(request);
        },
      },
      {
        id: 'decline',
        label: t('accessRequests.decline'),
        icon: XMarkIcon,
        onClick: (request: AccessRequest) => setToDecline(request),
        variant: 'destructive',
      },
    ],
    [t, workspaces, preferredWorkspaceId],
  );

  const workspaceOptions = (workspaces ?? []).map((w) => ({ value: w.id, label: w.name }));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('accessRequests.description')}</p>

      <div className="overflow-x-auto">
        <DataTable
          data={requests}
          columns={columns}
          actions={actions}
          isLoading={isLoading && requests.length === 0}
          error={loadFailed ? t('accessRequests.failedToLoad') : null}
          onRetry={fetchRequests}
          emptyMessage={t('accessRequests.none')}
          enablePagination={false}
          // Nothing sorts: the server returns oldest first, which is the order to answer in.
          sorting={[]}
          onSortingChange={() => {}}
        />
      </div>

      <FormModal
        isOpen={toApprove !== null}
        onClose={() => setToApprove(null)}
        title={t('accessRequests.approveTitle')}
        size="sm"
        submitText={t('accessRequests.approve')}
        cancelText={t('common.cancel')}
        canSubmit={!!workspaceId && actionInFlight === null}
        isLoading={toApprove !== null && actionInFlight === toApprove.id}
        onSubmit={handleApprove}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('accessRequests.approveDescription', { email: toApprove?.email ?? '' })}
          </p>
          <div className="space-y-2">
            <label htmlFor="access-request-workspace" className="block text-sm font-medium text-foreground">
              {t('accessRequests.workspace')} <span className="text-destructive">*</span>
            </label>
            {workspacesFailed ? (
              // Workspaces stays null, so clearing the flag re-runs the load.
              <RefreshErrorBanner
                message={t('accessRequests.failedToLoadWorkspaces')}
                onRetry={() => setWorkspacesFailed(false)}
              />
            ) : (
              <Select
                id="access-request-workspace"
                value={workspaceId}
                onChange={setWorkspaceId}
                options={workspaceOptions}
                disabled={workspaces === null}
                placeholder={workspaces === null ? t('accessRequests.loadingWorkspaces') : t('accessRequests.chooseWorkspace')}
              />
            )}
          </div>
          <div className="space-y-2">
            <label htmlFor="access-request-role" className="block text-sm font-medium text-foreground">
              {t('accessRequests.role')}
            </label>
            <Select
              id="access-request-role"
              value={role}
              onChange={(value) => setRole(value === 'admin' ? 'admin' : 'member')}
              options={[
                { value: 'member', label: t('accessRequests.roleMember') },
                { value: 'admin', label: t('accessRequests.roleAdmin') },
              ]}
            />
          </div>
        </div>
      </FormModal>

      <ConfirmationModal
        isOpen={toDecline !== null}
        onClose={() => setToDecline(null)}
        title={t('accessRequests.declineTitle')}
        description={t('accessRequests.declineDescription', { email: toDecline?.email ?? '' })}
        variant="danger"
        confirmText={t('accessRequests.decline')}
        cancelText={t('common.cancel')}
        onConfirm={handleDecline}
        isLoading={toDecline !== null && actionInFlight === toDecline.id}
      />
    </div>
  );
}
